// ============================================
// VOLUNTEER MODULE - DASHBOARD
// Clickable events on calendar + sidebar
// ============================================

import React, { useEffect, useState } from "react";
import { useTheme } from "../components/ThemeContext";
import { getStats, getEvents } from "../services/api";

const tColor = (t,C) => ({ Conference:C.accent, Festival:C.orange, Sports:C.green, Religious:C.purple, Music:C.red, Exhibition:C.purple, Other:C.muted }[t]||C.accent);
const sColor = (s,C) => ({ Active:C.green, Draft:C.orange, Completed:C.accent, Cancelled:C.red }[s]||C.muted);
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export default function Dashboard({ onViewEvent, onActiveEventChange }) {
  const { C } = useTheme();
  const [stats, setStats]           = useState({ totalVolunteers:0, activeEvents:0, totalGates:0, todayAssigned:0, todayPresent:0, todayAbsent:0, todayLate:0, whatsappSent:0 });
  const [events, setEvents]         = useState([]);
  const [currentMonth, setCurrMonth] = useState(new Date());
  const [selectedDay, setSelDay]    = useState(null);
  const [dayEvents, setDayEvents]   = useState([]);

  useEffect(() => {
    getStats().then(r => { if(r.data.success) setStats(r.data.stats); }).catch(()=>{});
    getEvents().then(r => {
      if(r.data.success) {
        const evs = r.data.events||[];
        setEvents(evs);
      }
    }).catch(()=>{});
  }, []);

  const today = new Date();
  const year  = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDay  = new Date(year,month,1).getDay();
  const daysInMon = new Date(year,month+1,0).getDate();

  const getEventsForDay = (day) => {
    const date = new Date(year,month,day); date.setHours(0,0,0,0);
    return events.filter(ev => {
      if(!ev.startDate) return false;
      // Hide cancelled events from the calendar entirely
      if(ev.status === "Cancelled") return false;
      const s = new Date(ev.startDate); s.setHours(0,0,0,0);
      const e = ev.endDate ? new Date(ev.endDate) : new Date(s); e.setHours(23,59,59,999);
      return date >= s && date <= e;
    });
  };

  const handleDayClick = (day) => {
    if(!day) return;
    const evs = getEventsForDay(day);
    setSelDay(d => d===day ? null : day);
    setDayEvents(evs);
  };

  const cells = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length:daysInMon }, (_,i)=>i+1),
  ];
  while(cells.length%7!==0) cells.push(null);

  // ── Single source of truth for event classification ─────────
  // An event is shown in exactly one bucket: Active, Upcoming, or Past.
  // Rules respect BOTH the manual status field AND the dates:
  //
  //   ACTIVE   = status="Active" AND today within [startDate, endDate]
  //   UPCOMING = status NOT in ["Completed","Cancelled"] AND startDate is in the future
  //   PAST     = endDate has passed OR status in ["Completed","Cancelled"]
  //
  // Cancelled events never show as Active or Upcoming.
  // Draft events that are still upcoming will appear in Upcoming.
  const now = new Date(); now.setHours(0,0,0,0);

  const dateOf = (s) => { if(!s) return null; const d=new Date(s); d.setHours(0,0,0,0); return d; };
  const endOf  = (s) => { if(!s) return null; const d=new Date(s); d.setHours(23,59,59,999); return d; };

  const isActive = (ev) => {
    if(ev.status !== "Active") return false;
    const s = dateOf(ev.startDate); if(!s) return false;
    const e = endOf(ev.endDate || ev.startDate);
    return now >= s && now <= e;
  };
  const isUpcoming = (ev) => {
    if(ev.status === "Completed" || ev.status === "Cancelled") return false;
    const s = dateOf(ev.startDate); if(!s) return false;
    return s > now;
  };
  const isPast = (ev) => {
    if(ev.status === "Completed" || ev.status === "Cancelled") return true;
    const e = endOf(ev.endDate || ev.startDate);
    return e ? e < now : false;
  };

  const activeEvs   = events.filter(isActive)
    .sort((a,b) => new Date(a.startDate) - new Date(b.startDate));
  const upcomingEvs = events.filter(isUpcoming)
    .sort((a,b) => new Date(a.startDate) - new Date(b.startDate))
    .slice(0,5);
  const pastEvs     = events.filter(isPast)
    .sort((a,b) => new Date(b.endDate || b.startDate) - new Date(a.endDate || a.startDate))
    .slice(0,3);

  // Notify parent (App.js → Sidebar) of the current active event
  // Single useEffect that fires when the *identity* of the first active event changes
  const activeEventId = activeEvs[0]?.id || null;
  useEffect(() => {
    if(onActiveEventChange) onActiveEventChange(activeEvs[0] || null);
  }, [activeEventId]); // eslint-disable-line

  const StatCard = ({ label, value, sub, color, icon }) => (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"14px 18px", flex:1, minWidth:120, position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:0, right:0, width:60, height:60, background:`radial-gradient(circle at top right, ${color}20, transparent 70%)` }} />
      <div style={{ fontSize:18, marginBottom:5 }}>{icon}</div>
      <div style={{ fontSize:22, fontWeight:800, color, fontFamily:"Georgia, serif" }}>{value}</div>
      <div style={{ fontSize:12, color:C.text, fontWeight:600, marginTop:1 }}>{label}</div>
      {sub && <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{sub}</div>}
    </div>
  );

  // Clickable event pill on calendar — green for Active, normal for Upcoming
  const EventPill = ({ ev }) => {
    const active = isActive(ev);
    const color  = active ? C.green : tColor(ev.eventType,C);
    return (
      <div
        onClick={e => { e.stopPropagation(); onViewEvent && onViewEvent(ev); }}
        title={`${active ? "Active now: " : "Click to view: "}${ev.name}`}
        style={{ background:`${color}22`, border:`1px solid ${color}44`, borderLeft:`3px solid ${color}`, borderRadius:4, padding:"2px 5px", fontSize:9, fontWeight:700, color, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", cursor:"pointer", transition:"background 0.15s" }}
        onMouseEnter={e=>e.currentTarget.style.background=`${color}44`}
        onMouseLeave={e=>e.currentTarget.style.background=`${color}22`}>
        {active && "● "}{ev.name}
      </div>
    );
  };

  // Clickable event row in sidebar panels
  const EventRow = ({ ev, showDiff=false }) => {
    const s = new Date(ev.startDate||0); s.setHours(0,0,0,0);
    const diff = Math.ceil((s-now)/(1000*60*60*24));
    return (
      <div
        onClick={() => onViewEvent && onViewEvent(ev)}
        style={{ padding:"11px 16px", borderBottom:`1px solid ${C.border}`, cursor:"pointer", transition:"background 0.15s" }}
        onMouseEnter={e=>e.currentTarget.style.background=C.border+"44"}
        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.accent, textDecoration:"underline", textDecorationStyle:"dotted", textUnderlineOffset:3 }}>{ev.name}</div>
            <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{ev.startDate}{ev.endDate&&ev.endDate!==ev.startDate?` → ${ev.endDate}`:""}</div>
          </div>
          {showDiff && diff>0 && (
            <span style={{ fontSize:11, fontWeight:700, color:diff<=3?C.orange:C.muted, marginLeft:8, flexShrink:0 }}>
              {diff===1?"Tomorrow":`${diff}d`}
            </span>
          )}
        </div>
        <div style={{ display:"flex", gap:4, marginTop:5, flexWrap:"wrap" }}>
          <span className="vh-badge" style={{ background:`${sColor(ev.status,C)}22`, color:sColor(ev.status,C), border:`1px solid ${sColor(ev.status,C)}44` }}>{ev.status}</span>
          {ev.eventType && <span className="vh-badge" style={{ background:`${tColor(ev.eventType,C)}22`, color:tColor(ev.eventType,C), border:`1px solid ${tColor(ev.eventType,C)}44` }}>{ev.eventType}</span>}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:22, fontWeight:800, color:C.text, fontFamily:"Georgia, serif" }}>Dashboard</div>
        <div style={{ fontSize:13, color:C.muted, marginTop:3 }}>
          {today.toLocaleDateString("en-US",{ weekday:"long", year:"numeric", month:"long", day:"numeric" })}
        </div>
      </div>

      {/* Active Event Alert */}
      {activeEvs.length > 0 && (
        <div
          onClick={() => onViewEvent && onViewEvent(activeEvs[0])}
          style={{ padding:"12px 18px", background:C.greenSoft, border:`1px solid ${C.green}44`, borderRadius:12, marginBottom:16, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", cursor:"pointer", transition:"background 0.2s" }}
          onMouseEnter={e=>e.currentTarget.style.background=`${C.green}33`}
          onMouseLeave={e=>e.currentTarget.style.background=C.greenSoft}>
          <div style={{ fontSize:18 }}>🟢</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.green }}>{activeEvs.length} event{activeEvs.length>1?"s":""} happening right now · Click to view</div>
            <div style={{ fontSize:12, color:C.muted }}>{activeEvs.map(e=>e.name).join(" · ")}</div>
          </div>
          <span style={{ fontSize:12, color:C.green, fontWeight:600 }}>View →</span>
        </div>
      )}

      {/* Stat Cards */}
      <div className="stats-grid" style={{ display:"flex", gap:12, marginBottom:18, flexWrap:"wrap" }}>
        <StatCard label="Active Volunteers" value={stats.totalVolunteers} sub="Arrived & active"   color={C.accent}  icon="👥" />
        <StatCard label="Active Events"     value={stats.activeEvents}    sub="Running now"        color={C.green}   icon="📅" />
        <StatCard label="Assigned Today"    value={stats.todayAssigned}   sub="Today's duties"     color={C.orange}  icon="🗂️" />
        <StatCard label="Present Today"     value={stats.todayPresent}    sub="Checked in today"   color={C.green}   icon="✅" />
        <StatCard label="WhatsApp Sent"     value={stats.whatsappSent}    sub="Today"              color={C.purple}  icon="💬" />
      </div>

      {/* Calendar + Sidebar */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 280px", gap:20 }} className="calendar-grid">

        {/* CALENDAR */}
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, overflow:"hidden" }}>

          {/* Nav */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 18px", borderBottom:`1px solid ${C.border}` }}>
            <div style={{ fontSize:15, fontWeight:800, color:C.text }}>{MONTHS[month]} {year}</div>
            <div style={{ display:"flex", gap:6 }}>
              <button onClick={()=>setCurrMonth(new Date(year,month-1,1))} style={{ padding:"5px 11px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:13, fontWeight:700, cursor:"pointer" }}>‹</button>
              <button onClick={()=>setCurrMonth(new Date())} style={{ padding:"5px 10px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>Today</button>
              <button onClick={()=>setCurrMonth(new Date(year,month+1,1))} style={{ padding:"5px 11px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:13, fontWeight:700, cursor:"pointer" }}>›</button>
            </div>
          </div>

          {/* Day names */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", borderBottom:`1px solid ${C.border}` }}>
            {DAYS.map(d => (
              <div key={d} style={{ padding:"8px 0", textAlign:"center", fontSize:11, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.4px" }}>{d}</div>
            ))}
          </div>

          {/* Grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)" }}>
            {cells.map((day,idx) => {
              const evs     = day ? getEventsForDay(day) : [];
              const isToday    = day && today.getDate()===day && today.getMonth()===month && today.getFullYear()===year;
              const dayInPast  = day && new Date(year,month,day) < now;
              const isSel      = day && selectedDay===day;
              // Highlight days that have ACTIVE events (running today onwards within bounds)
              const hasActiveEv = evs.some(isActive);

              return (
                <div key={idx} onClick={() => handleDayClick(day)}
                  style={{ minHeight:80, padding:"6px 5px", borderRight:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`,
                    background: isSel ? C.accentSoft
                              : hasActiveEv ? `${C.green}15`
                              : isToday ? `${C.accent}11`
                              : "transparent",
                    opacity: !day ? 0.2 : dayInPast && !evs.length ? 0.55 : 1,
                    cursor: day ? "pointer" : "default",
                    transition: "background 0.15s" }}>
                  {day && (
                    <>
                      <div style={{ width:24, height:24, borderRadius:"50%", background:isToday?C.accent:"transparent", color:isToday?"#000":dayInPast?C.muted:C.text, fontSize:12, fontWeight:isToday?800:500, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:3 }}>
                        {day}
                      </div>
                      {evs.slice(0,2).map((ev,j) => <EventPill key={j} ev={ev} />)}
                      {evs.length>2 && <div style={{ fontSize:9, color:C.muted, fontWeight:600 }}>+{evs.length-2} more</div>}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selected day panel */}
          {selectedDay && (
            <div style={{ padding:"14px 18px", borderTop:`1px solid ${C.border}`, background:C.bg }}>
              {dayEvents.length===0 ? (
                <div style={{ fontSize:12, color:C.muted }}>No events on {MONTHS[month]} {selectedDay} — click another day.</div>
              ) : (
                <>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:8 }}>Events on {MONTHS[month]} {selectedDay}, {year}</div>
                  {dayEvents.map((ev,i) => (
                    <div key={i}
                      onClick={() => onViewEvent && onViewEvent(ev)}
                      style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:C.card, border:`1px solid ${C.border}`, borderRadius:9, marginBottom:6, cursor:"pointer", transition:"border 0.15s" }}
                      onMouseEnter={e=>e.currentTarget.style.borderColor=C.accent}
                      onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:C.accent }}>{ev.name}</div>
                        <div style={{ fontSize:11, color:C.muted }}>{ev.startDate}{ev.endDate&&ev.endDate!==ev.startDate?` → ${ev.endDate}`:""}</div>
                      </div>
                      <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                        <span className="vh-badge" style={{ background:`${sColor(ev.status,C)}22`, color:sColor(ev.status,C), border:`1px solid ${sColor(ev.status,C)}44` }}>{ev.status}</span>
                        <span style={{ fontSize:11, color:C.accent, fontWeight:600 }}>View →</span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Legend */}
          <div style={{ padding:"10px 18px", borderTop:`1px solid ${C.border}`, display:"flex", gap:12, flexWrap:"wrap" }}>
            <div style={{ fontSize:11, color:C.muted, fontWeight:600 }}>Click an event pill to view details</div>
            {["Conference","Festival","Sports","Religious","Music"].map(type => (
              <div key={type} style={{ display:"flex", alignItems:"center", gap:4 }}>
                <div style={{ width:8, height:8, borderRadius:2, background:tColor(type,C) }} />
                <span style={{ fontSize:10, color:C.muted }}>{type}</span>
              </div>
            ))}
          </div>
        </div>

        {/* SIDEBAR */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* Active Now */}
          {activeEvs.length>0 && (
            <div style={{ background:C.card, border:`1px solid ${C.green}44`, borderRadius:14, overflow:"hidden" }}>
              <div style={{ padding:"11px 16px", borderBottom:`1px solid ${C.border}`, fontSize:12, fontWeight:700, color:C.green }}>🟢 Happening Now</div>
              {activeEvs.map((ev,i) => <EventRow key={i} ev={ev} />)}
            </div>
          )}

          {/* Upcoming */}
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, overflow:"hidden" }}>
            <div style={{ padding:"11px 16px", borderBottom:`1px solid ${C.border}`, fontSize:12, fontWeight:700, color:C.text }}>📅 Upcoming Events</div>
            {upcomingEvs.length===0 ? (
              <div style={{ padding:"16px", fontSize:12, color:C.muted, textAlign:"center" }}>No upcoming events</div>
            ) : upcomingEvs.map((ev,i) => <EventRow key={i} ev={ev} showDiff />)}
          </div>

          {/* Past */}
          {pastEvs.length>0 && (
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, overflow:"hidden" }}>
              <div style={{ padding:"11px 16px", borderBottom:`1px solid ${C.border}`, fontSize:12, fontWeight:700, color:C.muted }}>🕐 Past Events</div>
              {pastEvs.map((ev,i) => (
                <div key={i} style={{ opacity:0.7 }}><EventRow ev={ev} /></div>
              ))}
            </div>
          )}

          {/* Today Attendance */}
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"14px 16px" }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:12 }}>✅ Today's Attendance</div>
            {[
              { label:"Present", value:stats.todayPresent,  color:C.green },
              { label:"Absent",  value:stats.todayAbsent,   color:C.red },
              { label:"Late",    value:stats.todayLate,     color:C.orange },
            ].map((item,i) => {
              const total = stats.todayPresent+stats.todayAbsent+stats.todayLate||1;
              const pct   = Math.round((item.value/total)*100);
              return (
                <div key={i} style={{ marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:12, color:C.text }}>{item.label}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:item.color }}>{item.value}</span>
                  </div>
                  <div style={{ background:C.border, borderRadius:99, height:5 }}>
                    <div style={{ width:`${pct}%`, background:item.color, borderRadius:99, height:"100%", transition:"width 0.8s" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}